/**
 * @fileoverview RecentDeals component
 * Smart component that orchestrates the recent deals feature
 * Manages data fetching via useRecentDeals hook and renders child components
 * Handles visibility state and deal opening interactions
 *
 * Serves as the main container component for the recent deals slideout view
 * that displays a list of recently modified deals for the dealership
 */

// Enternal
import { DealershipPicker } from '@makemydeal/dr-activities-dealership-picker';
import { DealershipPickerMode } from '@makemydeal/dr-shared-types';
// Internal components
import { RecentDealsHeader } from '../RecentDealsHeader/RecentDealsHeader';
import RecentDealsList from '../RecentDealsList/RecentDealsList';
import RecentDealsSlideout from '../RecentDealsSlideout/RecentDealsSlideout';
import RecentDealsDocked from '../RecentDealsDocked/RecentDealsDocked';

// Hooks
import { useRecentDeals } from '../../hooks/useRecentDeals';

// Constants
import { SLIDEOUT_FOOTER_LAYOUT } from '../../constants';
import { RecentDealsVariant } from '../../enum/common.enum';

// Types
import type { RecentDealsProps, RecentDealsSlideoutProps } from '../../types/RecentDealsProps';

/**
 * RecentDeals component
 * Main smart component that:
 * - Fetches recent deals data via useRecentDeals hook
 * - Manages slideout visibility state
 * - Handles deal opening in same tab or new tab
 * - Renders header, deals list, loading spinner, and error states
 * - Uses Suspense for lazy-loaded content
 *
 * The component is hidden entirely when showRecentDeals is false (controlled by Redux)
 *
 * @returns RecentDeals slideout with header, content, and Suspense boundary, or null if hidden
 */
const RecentDeals: React.FC<RecentDealsProps> = ({ variant = RecentDealsVariant.OVERLAY }) => {
    const recentDealsState = useRecentDeals();

    if (!recentDealsState.showRecentDeals) {
        return null;
    }

    const panelProps: RecentDealsSlideoutProps = {
        isLoading: recentDealsState.isLoading,
        header: (
            <RecentDealsHeader title={recentDealsState.recentDealsTitle} redirectToDealList={recentDealsState.redirectToDealList} />
        ),
        filter: recentDealsState.showDealershipPicker ? (
            <DealershipPicker
                label="Dealership"
                value={recentDealsState.selectedDealership?.mmdId}
                onChangeDealership={recentDealsState.handleChangeDealership}
                mode={DealershipPickerMode.ALL_DEALERSHIPS}
            />
        ) : null,
        footer: {
            layout: SLIDEOUT_FOOTER_LAYOUT,
            primary: {
                action: () => recentDealsState.handleOpenDealInNewTab(),
                disabled: !recentDealsState.hasOpenDealEnabled,
                label: 'Open Deal'
            },
            ...(recentDealsState.isQuoteModeEnabled && {
                options: [
                    { label: 'Cancel', action: recentDealsState.handleCancel, buttonStyle: 'tertiary' as const },
                    { label: 'Create a Quote', action: recentDealsState.handleCreateQuote, buttonStyle: 'secondary' as const }
                ]
            })
        },
        children: (
            <RecentDealsList
                deals={recentDealsState.recentDeals}
                errorDetails={recentDealsState.errorDetails}
                onOpenDeal={recentDealsState.openDeal}
            />
        )
    };

    if (variant === RecentDealsVariant.DOCKED) {
        return <RecentDealsDocked {...panelProps} />;
    }

    return <RecentDealsSlideout {...panelProps} />;
};

export default RecentDeals;
